// Vibe pad — a 2D joystick between four editable text anchors. Anchors are
// embedded once (search.embedTexts) and cached; dragging blends them client-side
// (bilinear) and re-queries with the raw vector, so the wall morphs live.
import { useRef, useState } from "preact/hooks";
import { api } from "../../api";
import { Grid, type Item } from "./Grid";
import { blendBilinear } from "./vecMath";

const DEFAULT_ANCHORS = ["calm", "dramatic", "minimal", "detailed"] as const;
const CORNER_LABELS = ["top left", "top right", "bottom left", "bottom right"];

interface VibePadProps {
  gated: boolean;
  onSeed: (item: Item) => void;
}

export function VibePad({ gated, onSeed }: VibePadProps) {
  const [anchors, setAnchors] = useState<string[]>([...DEFAULT_ANCHORS]);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const [items, setItems] = useState<Item[]>([]);
  const [status, setStatus] = useState("drag the dot to explore");
  const vecCache = useRef(new Map<string, number[]>());
  const inFlight = useRef(false);
  const queued = useRef<{ x: number; y: number } | null>(null);

  async function anchorVecs(): Promise<[number[], number[], number[], number[]] | null> {
    const texts = anchors.map((a) => a.trim()).filter(Boolean);
    if (texts.length !== 4) return null;
    const missing = texts.filter((t) => !vecCache.current.has(t));
    if (missing.length > 0) {
      setStatus("embedding anchors… (first call after idle can take ~1 min)");
      const res = await api.search.embedTexts({ texts: missing });
      missing.forEach((t, i) => vecCache.current.set(t, res.vecs[i]!));
    }
    return texts.map((t) => vecCache.current.get(t)!) as [number[], number[], number[], number[]];
  }

  async function query(x: number, y: number) {
    if (inFlight.current) {
      queued.current = { x, y };
      return;
    }
    inFlight.current = true;
    try {
      const corners = await anchorVecs();
      if (!corners) return;
      const vec = blendBilinear(corners, x, y);
      const res = await api.search.vector({ vec, gated, k: 24 });
      setItems(res.items);
      setStatus(`${res.items.length} results (${res.tookMs} ms)`);
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      inFlight.current = false;
      const next = queued.current;
      queued.current = null;
      if (next) void query(next.x, next.y);
    }
  }

  function onPointer(e: PointerEvent, target: HTMLDivElement) {
    if (e.buttons === 0 && e.type === "pointermove") return;
    const r = target.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    setPos({ x, y });
    void query(x, y);
  }

  const setAnchor = (i: number, v: string) => {
    const next = [...anchors];
    next[i] = v;
    setAnchors(next);
  };

  return (
    <div>
      <p class="ls-meta">
        Four moods, one map: the dot position blends the four anchor vectors and re-ranks all 335k
        artworks live. Edit the corner words to change the space.
      </p>
      <div class="ls-row">
        {anchors.map((a, i) => (
          <input
            key={i}
            type="text"
            style="max-width:150px"
            value={a}
            placeholder={CORNER_LABELS[i]}
            onInput={(e) => setAnchor(i, (e.target as HTMLInputElement).value)}
            onBlur={() => void query(pos.x, pos.y)}
          />
        ))}
      </div>
      <div class="ls-row" style="align-items:flex-start">
        <div
          class="ls-pad"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            onPointer(e, e.currentTarget);
          }}
          onPointerMove={(e) => onPointer(e, e.currentTarget)}
        >
          <span class="ls-pad-label" style="top:4px;left:6px">
            {anchors[0]}
          </span>
          <span class="ls-pad-label" style="top:4px;right:6px">
            {anchors[1]}
          </span>
          <span class="ls-pad-label" style="bottom:4px;left:6px">
            {anchors[2]}
          </span>
          <span class="ls-pad-label" style="bottom:4px;right:6px">
            {anchors[3]}
          </span>
          <span class="ls-pad-dot" style={`left:${pos.x * 100}%;top:${pos.y * 100}%`}></span>
        </div>
        <p class="ls-meta" style="flex:1">
          {status}
        </p>
      </div>
      <Grid items={items} onCell={(it) => onSeed(it)} />
    </div>
  );
}
